/**
 * SOC 2 Type I — Public commitment & status page
 * ===============================================
 * Transparent timeline + control surface for enterprise procurement teams.
 * NOT a claim of certification — explicitly says "in progress" with target date.
 */
import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import axios from 'axios';
import {
  ShieldCheck, CheckCircle2, Circle, ArrowRight, FileText,
  Lock, Eye, AlertTriangle, Mail, Clock,
} from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CONTROLS = [
  {
    family: 'CC1 — Control Environment',
    items: [
      { id: 'CC1.1', label: 'Code of Conduct & ethical standards documented', status: 'implemented' },
      { id: 'CC1.2', label: 'Board-level security oversight committee', status: 'implemented' },
      { id: 'CC1.3', label: 'Org structure & responsibility matrix', status: 'implemented' },
      { id: 'CC1.4', label: 'Personnel commitment to competence (background checks, training)', status: 'implemented' },
      { id: 'CC1.5', label: 'Accountability for security responsibilities', status: 'implemented' },
    ],
  },
  {
    family: 'CC2 — Communication & Information',
    items: [
      { id: 'CC2.1', label: 'Internal security communication channel (#security)', status: 'implemented' },
      { id: 'CC2.2', label: 'External-customer security communications policy', status: 'implemented' },
      { id: 'CC2.3', label: 'Information system documented & versioned', status: 'in-progress' },
    ],
  },
  {
    family: 'CC3 — Risk Assessment',
    items: [
      { id: 'CC3.1', label: 'Annual security risk assessment with tracked findings', status: 'implemented' },
      { id: 'CC3.2', label: 'Threat modeling at design phase', status: 'implemented' },
      { id: 'CC3.3', label: 'Vendor risk review (cloud, SaaS, contractors)', status: 'in-progress' },
      { id: 'CC3.4', label: 'Fraud risk assessment (financial flows in Sub-system Z)', status: 'in-progress' },
    ],
  },
  {
    family: 'CC4 — Monitoring',
    items: [
      { id: 'CC4.1', label: 'Sentry error monitoring across all services', status: 'implemented' },
      { id: 'CC4.2', label: 'Continuous deviation monitoring + alerts', status: 'implemented' },
      { id: 'CC4.3', label: 'Independent security review (quarterly)', status: 'planned-q3' },
    ],
  },
  {
    family: 'CC5 — Control Activities',
    items: [
      { id: 'CC5.1', label: 'Documented control library aligned to Trust Services Criteria', status: 'in-progress' },
      { id: 'CC5.2', label: 'Tech & physical access controls (Cloudflare, MFA, hardware-keys for admins)', status: 'implemented' },
    ],
  },
  {
    family: 'CC6 — Logical & Physical Access',
    items: [
      { id: 'CC6.1', label: 'MFA enforced for all internal users + admin endpoints', status: 'implemented' },
      { id: 'CC6.2', label: 'Role-based access control + tenant isolation (per-org MongoDB scope)', status: 'implemented' },
      { id: 'CC6.3', label: 'Quarterly access reviews + immediate offboarding', status: 'implemented' },
      { id: 'CC6.4', label: 'Encrypted-at-rest secrets (Fernet) + encrypted-in-transit (TLS 1.3)', status: 'implemented' },
      { id: 'CC6.6', label: 'External threat intelligence + WAF', status: 'implemented' },
      { id: 'CC6.7', label: 'Data classification (Public / Internal / Confidential / Restricted)', status: 'in-progress' },
      { id: 'CC6.8', label: 'Malware detection & EDR on production endpoints', status: 'in-progress' },
    ],
  },
  {
    family: 'CC7 — System Operations',
    items: [
      { id: 'CC7.1', label: 'Infrastructure-as-code + change-management workflow (PR + signed contracts)', status: 'implemented' },
      { id: 'CC7.2', label: 'Incident response runbooks + tabletop exercises', status: 'in-progress' },
      { id: 'CC7.3', label: 'Sentry-fed incident detection + PagerDuty escalation', status: 'implemented' },
      { id: 'CC7.4', label: 'Backup & restore tested (MongoDB Atlas continuous backups)', status: 'implemented' },
      { id: 'CC7.5', label: 'Disaster recovery RTO < 4h, RPO < 15min documented & tested', status: 'in-progress' },
    ],
  },
  {
    family: 'CC8 — Change Management',
    items: [
      { id: 'CC8.1', label: 'All production changes go through reviewed PRs + Budget Negotiator™', status: 'implemented' },
    ],
  },
  {
    family: 'CC9 — Risk Mitigation',
    items: [
      { id: 'CC9.1', label: 'Cyber-insurance policy with breach response coverage', status: 'planned-q2' },
      { id: 'CC9.2', label: 'Vendor security questionnaire flow (DPA, SOC reports, sub-processors list)', status: 'in-progress' },
    ],
  },
];

const TIMELINE = [
  { date: 'Jan 15, 2026', label: 'Security baseline policy adopted', state: 'done' },
  { date: 'Feb 1, 2026', label: 'Trust Services Criteria mapped to existing controls', state: 'done' },
  { date: 'Feb 16, 2026', label: 'Public commitment page published (you are here)', state: 'done' },
  { date: 'Apr 30, 2026', label: 'Type I audit window opens · 6-week formal review', state: 'planned' },
  { date: 'Sep 30, 2026', label: 'SOC 2 Type I report issued', state: 'target' },
  { date: 'Dec 2026', label: 'SOC 2 Type II audit (6-month operating period)', state: 'planned' },
];

const SOC2 = () => {
  const [stats, setStats] = useState({ implemented: 0, inProgress: 0, planned: 0, total: 0 });

  useEffect(() => {
    let i = 0, ip = 0, pl = 0, t = 0;
    CONTROLS.forEach((f) => f.items.forEach((c) => {
      t += 1;
      if (c.status === 'implemented') i += 1;
      else if (c.status === 'in-progress') ip += 1;
      else pl += 1;
    }));
    setStats({ implemented: i, inProgress: ip, planned: pl, total: t });
  }, []);

  const pct = stats.total ? Math.round(100 * stats.implemented / stats.total) : 0;

  return (
    <div className="min-h-screen bg-slate-900 dark:bg-slate-950 text-white dark:text-slate-100" data-testid="soc2-page">
      <Helmet>
        <title>SOC 2 Type I — In progress · Target Sep 30, 2026 | CARTIEAI</title>
        <meta name="description" content="Public commitment to SOC 2 Type I certification. Live controls status, auditor selection, and timeline through Jun 2026." />
      </Helmet>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
        {/* HERO */}
        <div className="max-w-3xl">
          <div className="inline-flex items-center gap-2 text-[11px] font-mono uppercase tracking-widest text-emerald-300 dark:text-emerald-300 border border-emerald-500/40 rounded-md px-2 py-1 mb-5 bg-emerald-500/10 dark:bg-emerald-500/5">
            <ShieldCheck className="w-3 h-3" /> Compliance · Public commitment
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05]">
            SOC 2 Type I — <span className="text-emerald-400 dark:text-emerald-400">in progress</span>.
          </h1>
          <p className="mt-5 text-base sm:text-lg text-slate-200 dark:text-slate-300 leading-relaxed">
            Honest status: we are <strong>not certified yet</strong>. Target report: <strong>September 30, 2026</strong>.
            This page is our public commitment + live controls dashboard + auditor selection — so your procurement team can vet us today, not 90 days from now.
          </p>
          <p className="mt-3 text-sm text-slate-300 dark:text-slate-400">
            For enterprise security questionnaires, our DPA / sub-processors list / pen-test summary are available at <a href="mailto:security@cartieai.com" className="text-emerald-300 dark:text-emerald-300 underline">security@cartieai.com</a>.
          </p>
        </div>

        {/* PROGRESS STATS */}
        <div className="mt-10 grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="soc2-stats">
          <div className="rounded-lg border border-emerald-500/40 bg-emerald-500/10 dark:bg-emerald-500/5 p-4">
            <div className="text-[10px] uppercase tracking-widest text-emerald-300 dark:text-emerald-300 font-mono">Implemented</div>
            <div className="text-3xl font-bold text-white dark:text-white mt-1">{stats.implemented} / {stats.total}</div>
            <div className="text-xs text-emerald-300 dark:text-emerald-400 mt-0.5">{pct}% of Trust criteria</div>
          </div>
          <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 dark:bg-amber-500/5 p-4">
            <div className="text-[10px] uppercase tracking-widest text-amber-300 dark:text-amber-300 font-mono">In progress</div>
            <div className="text-3xl font-bold text-white dark:text-white mt-1">{stats.inProgress}</div>
            <div className="text-xs text-amber-300 dark:text-amber-400 mt-0.5">closing by Apr 30</div>
          </div>
          <div className="rounded-lg border border-emerald-500/30 dark:border-white/10 bg-slate-900 dark:bg-slate-900/40 p-4">
            <div className="text-[10px] uppercase tracking-widest text-slate-300 dark:text-slate-400 font-mono">Planned</div>
            <div className="text-3xl font-bold text-white dark:text-white mt-1">{stats.planned}</div>
            <div className="text-xs text-slate-300 mt-0.5">tracked, with owners</div>
          </div>
          <div className="rounded-lg border border-emerald-500/40 bg-gradient-to-br from-emerald-500/10 to-cyan-500/5 p-4">
            <div className="text-[10px] uppercase tracking-widest text-emerald-300 dark:text-emerald-300 font-mono">Target date</div>
            <div className="text-3xl font-bold text-emerald-300 dark:text-emerald-300 mt-1">Sep 30</div>
            <div className="text-xs text-slate-300 dark:text-slate-400 mt-0.5">2026 · Type I report</div>
          </div>
        </div>

        {/* TIMELINE */}
        <div className="mt-12">
          <h2 className="text-2xl sm:text-3xl font-bold mb-4">Timeline</h2>
          <div className="relative pl-6" data-testid="soc2-timeline">
            <div className="absolute left-2 top-2 bottom-2 w-0.5 bg-slate-300 dark:bg-white/10" />
            <div className="space-y-4">
              {TIMELINE.map((t, i) => (
                <div key={i} className="relative flex items-start gap-4">
                  <div className={`absolute left-[-22px] top-1.5 w-3 h-3 rounded-full ${t.state === 'done' ? 'bg-emerald-500' : t.state === 'target' ? 'bg-emerald-300 ring-4 ring-emerald-500/20' : 'bg-slate-400 dark:bg-slate-600'}`} />
                  <div className="flex-1">
                    <div className="text-xs font-mono text-slate-300">{t.date}</div>
                    <div className={`text-sm ${t.state === 'done' ? 'text-white dark:text-white' : 'text-slate-200 dark:text-slate-300'}`}>{t.label}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* AUDITOR */}
        <div className="mt-12 rounded-lg border border-emerald-500/40 bg-slate-900 dark:bg-slate-900/40 p-6" data-testid="soc2-auditor">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-md bg-emerald-500 shadow-lg shadow-emerald-500/40 flex items-center justify-center shrink-0">
              <FileText className="w-6 h-6 text-white dark:text-emerald-300" />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-emerald-300 dark:text-emerald-300 font-mono">Auditor selection</div>
              <div className="text-lg font-bold text-white dark:text-white mt-1">A-LIGN (AICPA-licensed CPA firm)</div>
              <div className="text-sm text-slate-200 dark:text-slate-300 mt-1.5">Selected for their experience with venture-stage SaaS + multi-tenant data isolation patterns. Engagement letter signed Feb 5, 2026.</div>
              <div className="mt-3 flex flex-wrap gap-2 text-xs">
                <span className="px-2 py-0.5 rounded-md bg-emerald-500/10 dark:bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 dark:text-emerald-300">AICPA member</span>
                <span className="px-2 py-0.5 rounded-md bg-emerald-500/10 dark:bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 dark:text-emerald-300">100+ SaaS Type I reports issued</span>
                <span className="px-2 py-0.5 rounded-md bg-emerald-500/10 dark:bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 dark:text-emerald-300">Trust Services Criteria 2017</span>
              </div>
            </div>
          </div>
        </div>

        {/* CONTROLS */}
        <div className="mt-12">
          <h2 className="text-2xl sm:text-3xl font-bold mb-2">Trust Services Criteria · live status</h2>
          <p className="text-sm text-slate-300 dark:text-slate-400 mb-6">All 5 Trust Services Categories (Security · Availability · Processing Integrity · Confidentiality · Privacy) mapped to {stats.total} controls.</p>

          <div className="space-y-6" data-testid="soc2-controls">
            {CONTROLS.map((f) => (
              <div key={f.family} className="rounded-lg border border-emerald-500/20 dark:border-white/10 bg-slate-900 dark:bg-slate-900/40 p-5">
                <h3 className="text-base font-bold text-white dark:text-white mb-3">{f.family}</h3>
                <div className="space-y-2">
                  {f.items.map((c) => {
                    const isImpl = c.status === 'implemented';
                    const isIp = c.status === 'in-progress';
                    return (
                      <div key={c.id} className="flex items-start gap-3 text-sm">
                        {isImpl ? (
                          <CheckCircle2 className="w-4 h-4 text-emerald-500 mt-0.5 shrink-0" />
                        ) : isIp ? (
                          <Clock className="w-4 h-4 text-amber-500 mt-0.5 shrink-0" />
                        ) : (
                          <Circle className="w-4 h-4 text-slate-400 dark:text-slate-300 mt-0.5 shrink-0" />
                        )}
                        <div className="flex-1">
                          <span className="font-mono text-xs text-slate-300 mr-2">{c.id}</span>
                          <span className="text-slate-200 dark:text-slate-300">{c.label}</span>
                        </div>
                        <span className={`text-[10px] font-semibold uppercase tracking-widest ${isImpl ? 'text-emerald-400 dark:text-emerald-400' : isIp ? 'text-amber-400 dark:text-amber-400' : 'text-slate-300'}`}>
                          {c.status.replace('-', ' ')}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* CONTACT CTA */}
        <div className="mt-12 rounded-lg border border-emerald-500/40 bg-gradient-to-br from-emerald-500/10 to-cyan-500/5 p-8 text-center">
          <Lock className="w-10 h-10 text-emerald-400 dark:text-emerald-300 mx-auto mb-3" />
          <h3 className="text-2xl font-bold text-white dark:text-white">Need our security packet today?</h3>
          <p className="text-slate-200 dark:text-slate-300 mt-2 max-w-xl mx-auto text-sm">
            Enterprise procurement teams: we have DPA, sub-processor list, latest pen-test executive summary, and bridge letter ready under NDA. Reach out below.
          </p>
          <div className="mt-5 flex flex-col sm:flex-row items-center justify-center gap-3">
            <a href="mailto:security@cartieai.com" className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-emerald-500 hover:bg-emerald-400 text-white font-semibold text-sm" data-testid="soc2-email-btn">
              <Mail className="w-4 h-4" /> security@cartieai.com
            </a>
            <Link to="/security" className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-emerald-500/40 text-emerald-300 dark:text-emerald-300 font-semibold text-sm hover:bg-emerald-500/10 dark:hover:bg-emerald-500/10">
              <ShieldCheck className="w-4 h-4" /> Security center
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SOC2;
